import React from 'react';
import PropTypes from 'prop-types';
import useForm from '@afosto/utils-react/validation/useForm';
import AdjustmentsFormSection from './AdjustmentsFormSection';
import ConstraintsFormSection from './ConstraintsFormSection';
import DefaultFormSection from './DefaultFormSection';
import FiltersFormSection from './FiltersFormSection';
import Form from '@afosto/app-components-react/Form';
import validationSchema from './validations';

const formatFilters = filters =>
  filters.reduce((acc, filter) => {
    const { key, operator, values } = filter;
    const valueNotDefined = values === undefined || values === null;
    const emptyArrayValue = Array.isArray(values) && values?.length === 0;

    if (!key || !operator || valueNotDefined || emptyArrayValue) {
      return acc;
    }

    const integerFields = ['per'];
    const formattedValue = integerFields.includes(key) ? parseInt(values, 10) : values;

    return [
      ...acc,
      {
        key,
        operator,
        values: formattedValue,
      },
    ];
  }, []);

const formatConstraints = constraints =>
  constraints.reduce((acc, constraint) => {
    const { key, operator, values } = constraint;
    const valueNotDefined = values === undefined || values === null;
    const emptyArrayValue = Array.isArray(values) && values?.length === 0;

    if (!key || !operator || valueNotDefined || emptyArrayValue) {
      return acc;
    }

    const integerFields = ['quantity', 'amount', 'filtered_amount'];
    const formattedValue = integerFields.includes(key) ? [parseInt(values, 10)] : values;

    return [
      ...acc,
      {
        key,
        operator,
        values: formattedValue,
      },
    ];
  }, []);

const formatAdjustments = adjustments =>
  adjustments.reduce((acc, adjustment) => {
    const { reference, description, amount, isPercentage, isDiscount } = adjustment;
    const amountNotDefined = amount === undefined || amount === null;

    if (!description || amountNotDefined) {
      return acc;
    }

    return [
      ...acc,
      {
        reference,
        description,
        amount: !isPercentage ? parseInt(amount, 10) : amount,
        is_percentage: isPercentage,
        is_discount: isDiscount,
      },
    ];
  }, []);

const PricingRuleForm = ({ pricingRule, onSubmit, onCancel }) => {
  const form = useForm({
    validationSchema,
    defaultValues: {
      name: pricingRule?.name ?? '',
      type: 'enhanced',
      activeAt: pricingRule?.active_at ?? null,
      expiresAt: pricingRule?.expires_at ?? null,
      adjustments: pricingRule?.adjustments ?? [],
      constraints: pricingRule?.constraints ?? [],
      filters: pricingRule?.filters ?? [],
      applyTo: pricingRule?.apply_to ?? '',
      grantLimit: pricingRule?.grant_limit ?? 0,
      priority: pricingRule?.priority ?? 0,
      isLastRule: pricingRule?.is_last_rule ?? false,
    },
    onSubmit: data => {
      if (onSubmit) {
        onSubmit({
          ...data,
          adjustments: formatAdjustments(data.adjustments),
          constraints: formatConstraints(data.constraints),
          filters: formatFilters(data.filters),
          grantLimit: parseInt(data.grantLimit, 10),
          priority: parseInt(data.priority, 10),
        });
      }
    },
    onCancel,
  });

  return (
    <Form form={form}>
      <DefaultFormSection pricingRule={pricingRule} />
      <AdjustmentsFormSection />
      <ConstraintsFormSection />
      <FiltersFormSection />
    </Form>
  );
};

PricingRuleForm.propTypes = {
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  pricingRule: PropTypes.object,
};

PricingRuleForm.defaultProps = {
  onSubmit: undefined,
  onCancel: undefined,
  pricingRule: undefined,
};

export default PricingRuleForm;
